// theirs
import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import classNames from 'classnames'
import { IntersectionOptions } from 'react-intersection-observer'
// ours
import { CollectionContext, CollectionContextInterface } from '../../App'
import { eventBus } from '../../utils/event-bus'
import { appendLocationSingle } from '../../utils/append-location'
import { breakpoints, color } from '../../styles'

interface CanvasProps {
    className?: string
    testId?: string
    children?: React.ReactNode
    collectionContext?: CollectionContextInterface
    inViewOptions?: IntersectionOptions
}

const baseClass = 'canvas-background'

const StyledCanvas = styled.div<CanvasProps>`
    background-color: ${color.darkest};
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    -webkit-transition: background-image 0.5s ease-in-out;
    transition: background-image 0.5s ease-in-out;
    height: ${(props) => props.collectionContext?.dimensions.height}px;
    width: ${(props) => props.collectionContext?.dimensions.width}px;
    display: table;
    position: fixed;
    top: 0;
    left: 0;git stash
    z-index: 1;

    video {
        height: 100vh;
        width: 100%;
        object-fit: cover;

        @media (min-width: ${breakpoints.xxxl}) {
            
        }
    }
`

const Canvas = ({
    className,
    testId,
    children,
    inViewOptions,
}: CanvasProps) => {
    // viewport using Context.API
    const collectionContext = React.useContext(CollectionContext)

    // local use state for canvas type
    const [canvasType, setCanvasType] = useState({ message: '' })

    // local use reference for checking if new frame is the same type of canvas type
    const prevCanvasTypeRef = useRef({ message: '' })
    useEffect(() => {
        prevCanvasTypeRef.current = { message: canvasType.message }
    })

    // local use state for canvas type
    const [videoSource, setVideoSource] = useState('')
    const [posterSource, setPosterSource] = useState('')

    // subscribing to event listener for when canvas type changes (when new frame is now on viewport)
    useEffect(() => {
        eventBus.on('canvasType', (data) => {
            if (data.message !== prevCanvasTypeRef.current.message) {
                setCanvasType({ message: data.message })
            }
        })

        let absolutePathVideoSource = ''
        let absolutePathPosterSource = ''
        // let isLandscape = collectionContext?.dimensions.height < collectionContext?.dimensions.width;
        
        switch (canvasType.message) {
            case 'DayClouds': {
                absolutePathVideoSource = appendLocationSingle(
                    'images',
                    collectionContext?.isLandscape ?  
                    'DayClouds-v5-1920x1080-15s.mp4':
                    'DayClouds-Portrait-1080x1920.mp4'
                )
                setVideoSource(absolutePathVideoSource)
                absolutePathPosterSource = appendLocationSingle(
                    'images',
                    collectionContext?.isLandscape ?  
                    'DayClouds-v3-1920x1080-15s.jpg':
                    'DayClouds-Portrait-1080x1920.jpg'
                )
                setPosterSource(absolutePathPosterSource)

                break
            }
            case 'DayMountains': {
                absolutePathVideoSource = appendLocationSingle(
                    'images',
                    collectionContext?.isLandscape ?  
                    'DayMountains-v5-1920x1080-10s.mp4':
                    'DayMountains-Portrait-1080x1920.mp4'
                )
                setVideoSource(absolutePathVideoSource)
                absolutePathPosterSource = appendLocationSingle(
                    'images',
                    collectionContext?.isLandscape ?  
                    'DayMountains-v3-1920x1080-10s.jpg':
                    'DayMountains-Portrait-1080x1920.jpg'
                )
                setPosterSource(absolutePathPosterSource)

                break
            }
            case 'NightEmbers': {
                absolutePathVideoSource = appendLocationSingle(
                    'images',
                    collectionContext?.isLandscape ?  
                    'NightEmbers-v5-1920x1080-15s.mp4':
                    'NightEmbers-Portrait-1080x1920.mp4'
                )
                setVideoSource(absolutePathVideoSource)
                absolutePathPosterSource = appendLocationSingle(
                    'images',
                    collectionContext?.isLandscape ?  
                    'NightEmbers-v3-1920x1080-15s.jpg':
                    'NightEmbers-Portrait-1080x1920.jpg'
                )
                setPosterSource(absolutePathPosterSource)

                break
            }
            case 'NightRain': {
                absolutePathVideoSource = appendLocationSingle(
                    'images',
                    collectionContext?.isLandscape ?  
                    'NightRain-v5-1920x1080-14s.mp4':
                    'NightRain-Portrait-1080x1920.mp4'
                )
                setVideoSource(absolutePathVideoSource)
                absolutePathPosterSource = appendLocationSingle(
                    'images',
                    collectionContext?.isLandscape ?  
                    'NightRain-v4-1920x1080-14s.jpg':
                    'NightRain-Portrait-1080x1920.jpg'
                )
                setPosterSource(absolutePathPosterSource)

                break
            }
            case 'NightSky': {
                absolutePathVideoSource = appendLocationSingle(
                    'images',
                    collectionContext?.isLandscape ?  
                    'NightSky-v5-1922x1090-15s.mp4':
                    'NightSky-Portrait-1080x1920.mp4'
                )
                setVideoSource(absolutePathVideoSource)
                absolutePathPosterSource = appendLocationSingle(
                    'images',
                    collectionContext?.isLandscape ?  
                    'NightSky-v3-1922x1090-15s.jpg':
                    'NightSky-Portrait-1080x1920.jpg'
                )
                setPosterSource(absolutePathPosterSource)

                break
            }
            case 'NightWaves': {
                absolutePathVideoSource = appendLocationSingle(
                    'images',
                    collectionContext?.isLandscape ?  
                    'NightWaves-v5-1920x1080-15s.mp4':
                    'NightWaves-Portrait-1080x1920.mp4'
                )

                setVideoSource(absolutePathVideoSource)
                absolutePathPosterSource = appendLocationSingle(
                    'images',
                    collectionContext?.isLandscape ?  
                    'NightWaves-v3-1920x1080-15s.jpg':
                    'NightWaves-Portrait-1080x1920.jpg'
                )
                setPosterSource(absolutePathPosterSource)

                break
            }
            case 'NoCanvasInViewStopAnimation': {
                //setVideoSource('')
                break
            }
        }
    }, [canvasType.message, collectionContext?.isLandscape])

    return (
        <StyledCanvas
            className={classNames(baseClass, className)}
            data-testid={testId}
            collectionContext={collectionContext || undefined}
        >
            {videoSource && (
                <div
                    className={`video-wrapper ${canvasType.message.toLocaleLowerCase()}`}
                >
                    <video
                        playsInline
                        autoPlay
                        muted
                        loop
                        key={videoSource}
                        poster={posterSource}
                    >
                        <source type="video/mp4" src={videoSource} />
                    </video>
                </div>
            )}
        </StyledCanvas>
    )
}

export default Canvas
